import { deleteStepFromGraph } from './deleteStepFromGraph.js';
import { addStepToGraph } from './addStepToGraph.js';
import { cloneWorkflowGraph } from './cloneWorkflowGraph.js';
import { getStructuralDescendentIds } from './getStructuralDescendentIds.js';

export const normalizeReorderStepIds = ({ graph, stepIds = [] }) => {
  const uniqueStepIds = [];
  const seen = new Set();
  for (const stepId of stepIds || []) {
    if (!stepId || seen.has(stepId) || !graph?.[stepId]) {
      continue;
    }
    seen.add(stepId);
    uniqueStepIds.push(stepId);
  }

  return uniqueStepIds.filter((stepId) => {
    return !uniqueStepIds.some((otherStepId) => {
      if (otherStepId === stepId) {
        return false;
      }
      return getStructuralDescendentIds(graph, otherStepId).has(stepId);
    });
  });
};

export const validateReorderGraphRequest = ({ graph, stepIds = [], parentStepId, relation, index }) => {
  const normalizedStepIds = normalizeReorderStepIds({ graph, stepIds });

  if (normalizedStepIds.length === 0) {
    return { valid: false, reason: 'no_steps', stepIds: normalizedStepIds };
  }

  if (!['exit', 'child', 'branch'].includes(relation)) {
    return { valid: false, reason: 'invalid_relation', stepIds: normalizedStepIds };
  }

  if (relation === 'branch' && !Number.isInteger(index)) {
    return { valid: false, reason: 'invalid_branch_index', stepIds: normalizedStepIds };
  }

  if (parentStepId && !graph?.[parentStepId]) {
    return { valid: false, reason: 'missing_parent', stepIds: normalizedStepIds };
  }

  if (normalizedStepIds.includes(parentStepId)) {
    return { valid: false, reason: 'target_is_moved_step', stepIds: normalizedStepIds };
  }

  const targetInsideMovedStructure = normalizedStepIds.some((stepId) => {
    return getStructuralDescendentIds(graph, stepId).has(parentStepId);
  });

  if (targetInsideMovedStructure) {
    return { valid: false, reason: 'target_inside_moved_structure', stepIds: normalizedStepIds };
  }

  return { valid: true, stepIds: normalizedStepIds };
};

/**
 * Atomic two-phase reorder.
 *
 * Snapshot every moved step's pre-move adjacency once at entry, then run a clean
 * delete-all → insert-all pass on a fresh graph clone. This avoids the per-iteration
 * snapshot/mutate loop where one iteration's deletion could change topology under the
 * next iteration's feet (stale exit/child/branch pointers, accidental orphans the
 * downstream auto-fixer would then delete).
 *
 * Signature is unchanged: callers (drag-and-drop editor, reorder_steps tool executor)
 * receive a new graph object with the moved steps reattached at the target location
 * in sequence — stepIds[0] under parentStepId via the requested relation/index, then
 * stepIds[1..] chained as exits off the previous moved step.
 *
 * Input graph is NOT mutated.
 */
export const reorderGraph = ({ graph, stepIds = [], parentStepId, relation, index }) => {
  const validation = validateReorderGraphRequest({ graph, stepIds, parentStepId, relation, index });
  if (!validation.valid) {
    return cloneWorkflowGraph(graph);
  }
  const normalizedStepIds = validation.stepIds;

  // Snapshot all moved nodes BEFORE any mutation so their child/branch pointers reflect
  // pre-move adjacency, not whatever later iterations would have done to them.
  // `branches` is an array of step-id strings (see addStepToGraph / deleteStepFromGraph);
  // a shallow `[...graph[id].branches]` decouples the array identity. Without this,
  // addStepToGraph would write the snapshot directly into the working graph and the
  // returned graph's `.branches` would still alias the caller's input — a downstream
  // mutation of `result.X.branches` would leak back.
  const snapshots = {};
  for (const id of normalizedStepIds) {
    if (graph?.[id]) {
      snapshots[id] = {
        ...graph[id],
        branches: Array.isArray(graph[id]?.branches) ? [...graph[id].branches] : graph[id]?.branches
      };
    }
  }

  // Deep-clone every node we may mutate (existing parent + every node along the path
  // that delete/add will touch). The simplest correct approach is to deep-clone the
  // whole graph: addStepToGraph and deleteStepFromGraph mutate node objects in place,
  // so any shallow clone would still propagate mutations back to the caller's input.
  let workingGraph = cloneWorkflowGraph(graph);

  // Phase 1: detach every moved step from its current parent. Use shallow=true so
  // descendants (child / branches) survive — they will ride along inside the snapshot
  // when the step is reinserted.
  for (const id of normalizedStepIds) {
    if (workingGraph[id]) {
      workingGraph = deleteStepFromGraph(workingGraph, id, true);
    }
  }

  // Phase 2: reinsert each step in order. stepIds[0] uses the caller-supplied
  // relation/index against parentStepId; stepIds[1..] chain as 'exit' off the
  // previous moved step, producing the desired sequence.
  for (let i = 0; i < normalizedStepIds.length; i++) {
    const currentId = normalizedStepIds[i];
    if (!snapshots[currentId]) continue;
    workingGraph = addStepToGraph({
      graph: workingGraph,
      stepId: currentId,
      parentStepId: i === 0 ? parentStepId : normalizedStepIds[i - 1],
      relation: i === 0 ? relation : 'exit',
      index: i === 0 ? index : undefined,
      step: snapshots[currentId]
    });
  }

  return workingGraph;
};
